/**
 * Geometry utility functions for coordinate and distance calculations
 * Centralized module to eliminate duplication across the codebase
 */

import type { Coordinate } from '@/types'

/**
 * Calculate Euclidean distance between two points
 *
 * @param a - First point
 * @param b - Second point
 * @returns Distance between the two points
 *
 * @example
 * const dist = distance({ x: 0, y: 0 }, { x: 3, y: 4 })
 * // Returns: 5
 */
export function distance(a: Coordinate, b: Coordinate): number {
	const dx = b.x - a.x
	const dy = b.y - a.y
	return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Calculate squared distance between two points (avoids sqrt for performance)
 * Useful for distance comparisons where the actual distance value isn't needed
 *
 * @param a - First point
 * @param b - Second point
 * @returns Squared distance between the two points
 *
 * @example
 * // Faster than distance() when you only need to compare distances
 * if (distanceSquared(p1, p2) < threshold ** 2) {
 *   // p1 is closer than threshold
 * }
 */
export function distanceSquared(a: Coordinate, b: Coordinate): number {
	const dx = b.x - a.x
	const dy = b.y - a.y
	return dx * dx + dy * dy
}

/**
 * Calculate distance from a point with pre-computed dx/dy (optimization)
 * Useful when dx and dy are already calculated for other purposes
 *
 * @param dx - Delta x
 * @param dy - Delta y
 * @returns Distance
 *
 * @example
 * const dx = point.x - target.x
 * const dy = point.y - target.y
 * const dist = distanceFromDeltas(dx, dy)
 */
export function distanceFromDeltas(dx: number, dy: number): number {
	return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Check if a point is within a threshold distance of a target
 * Uses bounding box pre-filter for performance optimization
 *
 * @param position - Current position
 * @param target - Target position
 * @param threshold - Maximum distance threshold
 * @returns The distance if within threshold, null otherwise
 *
 * @example
 * const dist = distanceWithinThreshold(cursor, player, 10)
 * if (dist !== null) {
 *   console.log(`Within threshold: ${dist}`)
 * }
 */
export function distanceWithinThreshold(
	position: Coordinate,
	target: Coordinate,
	threshold: number,
): number | null {
	const dx = position.x - target.x
	const dy = position.y - target.y

	// Bounding box pre-filter: skip expensive sqrt if clearly outside threshold
	if (Math.abs(dx) > threshold || Math.abs(dy) > threshold) {
		return null
	}

	const dist = Math.sqrt(dx * dx + dy * dy)
	return dist <= threshold ? dist : null
}

/**
 * Calculate magnitude (length) of a vector
 * Alias for distance from origin
 *
 * @param vector - Vector coordinate
 * @returns Magnitude of the vector
 *
 * @example
 * const mag = magnitude({ x: 3, y: 4 })
 * // Returns: 5
 */
export function magnitude(vector: Coordinate): number {
	return Math.sqrt(vector.x * vector.x + vector.y * vector.y)
}

/**
 * Normalize a vector to unit length
 * Returns zero vector if magnitude is zero
 *
 * @param vector - Vector to normalize
 * @returns Normalized vector
 *
 * @example
 * const normalized = normalize({ x: 3, y: 4 })
 * // Returns: { x: 0.6, y: 0.8 }
 */
export function normalize(vector: Coordinate): Coordinate {
	const mag = magnitude(vector)
	if (mag === 0) {
		return { x: 0, y: 0 }
	}
	return { x: vector.x / mag, y: vector.y / mag }
}

/**
 * Calculate angle (in radians) from one point to another
 * Angle is measured counterclockwise from the positive X-axis
 *
 * @param from - Starting point
 * @param to - Ending point
 * @returns Angle in radians (-π to π)
 *
 * @example
 * const angle = angleBetweenPoints({ x: 0, y: 0 }, { x: 1, y: 1 })
 * // Returns: π/4 (45 degrees)
 */
export function angleBetweenPoints(from: Coordinate, to: Coordinate): number {
	return Math.atan2(to.y - from.y, to.x - from.x)
}

/**
 * Calculate angle (in radians) from a direction vector
 * Uses pre-computed dx and dy for performance
 *
 * @param dx - Delta x
 * @param dy - Delta y
 * @returns Angle in radians (-π to π)
 *
 * @example
 * const dx = 1, dy = 1
 * const angle = angleFromDeltas(dx, dy)
 * // Returns: π/4 (45 degrees)
 */
export function angleFromDeltas(dx: number, dy: number): number {
	return Math.atan2(dy, dx)
}

/**
 * Calculate angle (in degrees) from one point to another
 *
 * @param from - Starting point
 * @param to - Ending point
 * @returns Angle in degrees (-180 to 180)
 *
 * @example
 * const angle = angleBetweenPointsDegrees({ x: 0, y: 0 }, { x: 1, y: 0 })
 * // Returns: 0 (pointing right)
 */
export function angleBetweenPointsDegrees(from: Coordinate, to: Coordinate): number {
	return (angleBetweenPoints(from, to) * 180) / Math.PI
}

/**
 * Convert radians to degrees
 *
 * @param radians - Angle in radians
 * @returns Angle in degrees
 */
export function toDegrees(radians: number): number {
	return (radians * 180) / Math.PI
}

/**
 * Convert degrees to radians
 *
 * @param degrees - Angle in degrees
 * @returns Angle in radians
 */
export function toRadians(degrees: number): number {
	return (degrees * Math.PI) / 180
}

/**
 * Calculate perpendicular distance from a point to a line segment
 *
 * @param point - Point to measure from
 * @param lineStart - Start of line segment
 * @param lineEnd - End of line segment
 * @returns Perpendicular distance from point to line
 */
export function perpendicularDistance(
	point: Coordinate,
	lineStart: Coordinate,
	lineEnd: Coordinate,
): number {
	const area = Math.abs(
		(lineEnd.x - lineStart.x) * (lineStart.y - point.y) -
		(lineStart.x - point.x) * (lineEnd.y - lineStart.y),
	) / 2
	const base = distance(lineStart, lineEnd)
	if (base === 0) return distance(point, lineStart)
	return (2 * area) / base
}

/**
 * Get distance and parametric t value for closest point on segment.
 * Returns the distance from point to the closest point on the segment,
 * and the parametric value t (0-1) indicating position along the segment.
 *
 * @param point - Point to measure from
 * @param segStart - Start of line segment
 * @param segEnd - End of line segment
 * @returns Object with distance and parametric t value (0-1)
 */
export function pointToSegmentInfo(
	point: Coordinate,
	segStart: Coordinate,
	segEnd: Coordinate,
): { distance: number; t: number } {
	const dx = segEnd.x - segStart.x
	const dy = segEnd.y - segStart.y
	const lengthSq = dx * dx + dy * dy

	if (lengthSq === 0) {
		const dist = Math.sqrt(
			(point.x - segStart.x) ** 2 + (point.y - segStart.y) ** 2,
		)
		return { distance: dist, t: 0 }
	}

	let t = ((point.x - segStart.x) * dx + (point.y - segStart.y) * dy) / lengthSq
	t = Math.max(0, Math.min(1, t))

	const closestX = segStart.x + t * dx
	const closestY = segStart.y + t * dy
	const dist = distance(point, { x: closestX, y: closestY })

	return { distance: dist, t }
}
